import React, { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router';

import { store } from '../reducers';
import { changeCurrentOrganization } from '../reducers/organization/reducer';

import { useQuery } from '@tanstack/react-query';
import { organizationsQueryOptions } from '../queryOptions';

import {
  AppHeader,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import { BreadcrumbNav } from '../components/breadcrumbNav/BreadcrumbNav';
import UserMenu from '../components/userMenu/UserMenu';
import SideNav from '../components/sideNav/SideNav';
import { getSessionObject } from '../utilities/storage';
import NoOrganizations from '../components/utility/NoOrganizations';
import NavigationPromptContext from '../contexts/NavigationPromptContext';

/**
 * The main layout component that wraps the application with a header and footer
 * @returns {React.JSX.Element}
 */
export default function MainLayout() {
  const organizations = useQuery(organizationsQueryOptions());
  const [isFormDirty, setIsFormDirty] = useState(false);

  const NavigationPromptContextValue = useMemo(
    () => ({
      isFormDirty,
      setIsFormDirty,
    }),
    [isFormDirty],
  );

  useEffect(() => {
    if (organizations.isSuccess) {
      let currentOrganization = getSessionObject('currentOrganization');

      if (
        organizations.data.length &&
        (!currentOrganization ||
          !organizations.data.find((org) => org.key === currentOrganization))
      ) {
        currentOrganization = organizations.data[0].key;
      }

      store.dispatch(changeCurrentOrganization(currentOrganization));
    }
  }, [organizations.data, organizations.isSuccess]);

  return (
    <>
      <div className="sticky-header">
        <AppHeader
          title="ClinicalView"
          buttons={[<UserMenu key="user-menu" />]}
        />
      </div>
      <div className="main-content">
        <SideNav />
        <main>
          {organizations.isPending ? (
            <VStack
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
              height="100%"
              width="100%"
            >
              <LoadingSpinner testID="main-layout-loading-spinner" />
            </VStack>
          ) : null}

          {organizations.isError ||
          (organizations.isSuccess && organizations.data.length === 0) ? (
            <NoOrganizations />
          ) : null}

          {organizations.isSuccess && organizations.data.length > 0 ? (
            <NavigationPromptContext.Provider
              value={NavigationPromptContextValue}
            >
              <div className="breadcrumb-content">
                <BreadcrumbNav />
                <div className="page-content">
                  <Outlet />
                </div>
              </div>
            </NavigationPromptContext.Provider>
          ) : null}
        </main>
      </div>
    </>
  );
}
